import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';

import DialogManageJECellListOption from '@/components/Tables/Cells/ListCell/withManageListItemModal/DialogManageJECellListOption';
import type { JECellSearchHandler } from '@/components/Tables/Cells/ListCell/withManageListItemModal/form';

// Only the members this wrapper reads are declared: `input` and `params` arrive
// from the journal-entry cell above, which is still untyped.
type OwnProps = {
  documentID: string;
  input: { isCreatable: boolean };
  params: { cellName: string };
};

// `ComponentType<any>` for the wrapped component, deliberately: both call sites
// are untyped `.jsx` composed through redux's `compose`, which erases the
// generic anyway — a precise one would buy nothing and cost every reader.
const withManageListItemModal = (WrappedComponent: React.ComponentType<any>) => {
  const ManageListItemModal = (props: OwnProps) => {
    const { documentID, input, params } = props;
    const companyId = useSelector(currentCompanyIdSelector);

    const [isOpen, setIsOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    // '' asks for the create form; a set value asks for that row's edit form.
    // It is part of the query key, which is why it is state and not a ref.
    const [cellValue, setCellValue] = useState('');

    // The dialog's outcome, not the read: the cell awaits the user's Confirm and
    // receives the submitted values. The read is a query inside the dialog.
    const resolveRef = useRef<(values: unknown) => void>();
    const rejectRef = useRef<() => void>();

    const open = useCallback(
      (nextCellValue: string) =>
        new Promise((resolve, reject) => {
          resolveRef.current = resolve;
          rejectRef.current = reject;
          setCellValue(nextCellValue);
          setIsOpen(true);
        }),
      [],
    );

    const onCreate = useCallback(() => {
      if (input.isCreatable) return open('');

      // Never settles — carried across unchanged, so the cell's own `.then` and
      // `.catch` still both fail to run for a non-creatable cell.
      return new Promise<never>(() => {});
    }, [input.isCreatable, open]);

    const onEdit = useCallback(
      (nextCellValue: string) => {
        setIsEdit(true);

        return open(nextCellValue);
      },
      [open],
    );

    // Both callbacks below settle a promise, and a promise callback is a
    // microtask, so the cell reacts after React has committed the close — the
    // same order the previous class got from setState's callback.
    const cancel = useCallback(() => {
      setIsOpen(false);
      setIsEdit(false);
      rejectRef.current?.();
    }, []);

    // isEdit is deliberately not reset here — only cancelling clears it, which
    // is the previous behaviour.
    const onSubmit = useCallback((values: unknown) => {
      setIsOpen(false);
      resolveRef.current?.(values);
    }, []);

    return (
      <WrappedComponent {...props} onEdit={onEdit} onCreate={onCreate}>
        {(name: string, onSearchHandler: JECellSearchHandler) =>
          isOpen && (
            <DialogManageJECellListOption
              companyId={companyId}
              documentID={documentID}
              cellName={params.cellName}
              cellValue={cellValue}
              name={name}
              isEdit={isEdit}
              cancel={cancel}
              onSubmit={onSubmit}
              onSearchHandler={onSearchHandler}
            />
          )
        }
      </WrappedComponent>
    );
  };

  return ManageListItemModal;
};

export default withManageListItemModal;
